import React from "react";
import { Flex } from "@jsq/ds/core";
import { isOtherPaymentMethod } from "../../common/utils";
import { AchPaymentPrefDisplay } from "./AchPaymentPrefDisplay";
import { BacsPaymentPrefDisplay } from "./BacsPaymentPrefDisplay";
import { BecsPaymentPrefDisplay } from "./BecsPaymentPrefDisplay";
import { CheckPaymentPrefDisplay } from "./CheckPaymentPrefDisplay";
import { EftPaymentPrefDisplay } from "./EftPaymentPrefDisplay";
import { OtherPaymentPrefDisplay } from "./OtherPaymentPrefDisplay";
import { SepaPaymentPrefDisplay } from "./SepaPaymentPrefDisplay";
import { type PaymentPrefDetailsProps } from "./types";
import { WirePaymentPrefDisplay } from "./WirePaymentPrefDisplay";

/**
 * Component for displaying detailed payment preference information with configurable field visibility.
 *
 * This component automatically detects the payment type (ACH, Wire, Check, BACS, BECS, SEPA, or EFT) and
 * renders the appropriate fields. You can control which fields are displayed using the `visibleFields` prop.
 * If `visibleFields` is omitted, all fields will be shown.
 */
export const PaymentPrefDetails: React.FC<PaymentPrefDetailsProps> = ({
  paymentPrefFull,
  checkDisplayOverride = {},
  visibleFields,
  showBankDomicile,
}: PaymentPrefDetailsProps) => {
  const achPaymentPref =
    paymentPrefFull?.__typename === "AchPaymentPrefObjectType" ? paymentPrefFull : undefined;

  const wirePaymentPref =
    paymentPrefFull?.__typename === "WirePaymentPrefObjectType" ? paymentPrefFull : undefined;

  const checkPaymentPref =
    paymentPrefFull?.__typename === "CheckPaymentPrefObjectType" ? paymentPrefFull : undefined;

  const bacsPaymentPref =
    paymentPrefFull?.__typename === "BacsPaymentPrefObjectType" ? paymentPrefFull : undefined;

  const becsPaymentPref =
    paymentPrefFull?.__typename === "BecsPaymentPrefObjectType" ? paymentPrefFull : undefined;

  const sepaPaymentPref =
    paymentPrefFull?.__typename === "SepaPaymentPrefObjectType" ? paymentPrefFull : undefined;

  const eftPaymentPref =
    paymentPrefFull?.__typename === "EftPaymentPrefObjectType" ? paymentPrefFull : undefined;

  const otherPaymentPref =
    paymentPrefFull?.__typename === "NonUsPaymentPrefObjectType" &&
    isOtherPaymentMethod(paymentPrefFull)
      ? paymentPrefFull
      : undefined;

  const { checkAddressOverride, checkPayeeOverride } = checkDisplayOverride;

  return (
    <Flex flexDirection="column">
      {achPaymentPref && (
        <AchPaymentPrefDisplay
          achPaymentPrefFull={achPaymentPref}
          visibleFields={visibleFields?.ach}
        />
      )}
      {wirePaymentPref && (
        <WirePaymentPrefDisplay
          wirePaymentPrefFull={wirePaymentPref}
          visibleFields={visibleFields?.wire}
          showBankDomicile={showBankDomicile}
        />
      )}
      {checkPaymentPref && (
        <CheckPaymentPrefDisplay
          checkPaymentPrefFull={checkPaymentPref}
          checkAddressOverride={checkAddressOverride}
          checkPayeeOverride={checkPayeeOverride}
          visibleFields={visibleFields?.check}
          showBankDomicile={showBankDomicile}
        />
      )}
      {bacsPaymentPref && (
        <BacsPaymentPrefDisplay
          bacsPaymentPrefFull={bacsPaymentPref}
          visibleFields={visibleFields?.bacs}
        />
      )}
      {becsPaymentPref && (
        <BecsPaymentPrefDisplay
          becsPaymentPrefFull={becsPaymentPref}
          visibleFields={visibleFields?.becs}
        />
      )}
      {sepaPaymentPref && (
        <SepaPaymentPrefDisplay
          sepaPaymentPrefFull={sepaPaymentPref}
          visibleFields={visibleFields?.sepa}
        />
      )}
      {eftPaymentPref && (
        <EftPaymentPrefDisplay
          eftPaymentPrefFull={eftPaymentPref}
          visibleFields={visibleFields?.eft}
        />
      )}
      {otherPaymentPref && (
        <OtherPaymentPrefDisplay
          label={otherPaymentPref.otherPaymentPrefLabel ?? ""}
          instructions={otherPaymentPref.nonUsPaymentPrefInstructions}
        />
      )}
    </Flex>
  );
};
